import React, { useEffect, useCallback, useMemo, useState } from 'react';
import DeNovoHomeScreen from './DeNovoHomeScreen';
import { generateClozeHint } from '../utils/clozeUtils'; 
import { subjectMap } from '../constants';
import OnboardingTooltip from './OnboardingTooltip';
import * as storage from '../storageService'; 
import * as srsService from '../srsService'; 
import { addPoints } from '../storageService';
import './DeNovoView.css';

const TracerBar = ({ counts }) => (
    <div className="denovo-tracer-bar">
        <span className="tracer-item review">{counts.review} <span className="label">Due</span></span>
        <span className="tracer-item learning">{counts.learning} <span className="label">Lrn</span></span>
        <span className="tracer-item new">{counts.new} <span className="label">New</span></span>
    </div>
);

const ZenCard = ({ rule, isRevealed, onReveal, onEditRule }) => {
    const [revealedClozeIds, setRevealedClozeIds] = useState(new Set());
    useEffect(() => { setRevealedClozeIds(new Set()); }, [rule.id]);
    const isNewCard = !rule.srs_data?.lastReviewedDate;
    
    const handleCardClick = (e) => {
        if (isRevealed) return;
        const target = e.target.closest('.zen-cloze');
        if (target) {
            const id = parseInt(target.dataset.id, 10);
            if (!isNaN(id)) setRevealedClozeIds(prev => new Set(prev).add(id));
        }
    };
    
    const processedHtml = useMemo(() => {
        let counter = 0;
        return rule.content_html.replace(/\[(.*?)\]/g, (match, inner) => {
            const id = counter++, isVisible = isRevealed || revealedClozeIds.has(id);
            const content = isVisible ? inner : generateClozeHint(inner);
            return `<span class="zen-cloze ${isVisible ? 'revealed' : 'hidden'}" data-id="${id}">${content}</span>`;
        });
    }, [rule.content_html, isRevealed, revealedClozeIds]);

    return (
        <div className={`zen-card ${isNewCard ? 'is-new' : ''}`}>
            <div className="zen-card-top-fixed">
                <div className="zen-new-badge-container">
                    <div className="zen-header-tags">
                        {rule.isHFR && <span className="zen-tag hfr">HFR</span>}
                        {isNewCard && <span className="zen-tag new">Fast Track</span>}
                    </div>
                    {isNewCard && !isRevealed && (
                        <div className="new-card-alert alert-wrapper">
                            <span className="material-symbols-outlined">info</span>
                            <span className="alert-text">Click <span className="key-cap">clozes</span> to reveal.</span>
                        </div>
                    )}
                </div>
                <div className="zen-card-header">
                    <div className="zen-meta">{subjectMap[rule.subject] || rule.subject} <span className="separator">|</span> {rule.displayPath?.slice(-1)[0] || 'General'}</div>
                    <div className="zen-rule-title">{rule.ruleName}</div>
                </div>
            </div>
            <div className="zen-scroll-area" onClick={handleCardClick} dangerouslySetInnerHTML={{__html: processedHtml}} />
            <div className="zen-card-bottom-fixed">
                {isRevealed && (
                    <div className="zen-keynote-wrapper">
                        {rule.keynote && (
                            <div className="zen-keynote-container">
                                <div className="zen-keynote-label">My Keynote</div>
                                <div className="zen-keynote-text">{rule.keynote}</div>
                            </div>
                        )}
                        <button className="zen-edit-keynote-btn" onClick={() => onEditRule(rule.id)}><span className="material-symbols-outlined">edit_note</span>{rule.keynote ? "Edit Keynote" : "Add Keynote"}</button>
                    </div>
                )}
            </div>
        </div>
    );
};

const InfoModal = ({ onClose }) => (
    <div className="info-modal-overlay" onClick={onClose}>
        <div className="info-modal-content" onClick={e => e.stopPropagation()}>
            <div className="info-modal-header"><h3>How to use De Novo</h3><button className="close-btn" onClick={onClose}>×</button></div>
            <div className="info-modal-body">
                <p><strong>De Novo</strong> is your active recall engine. Here is how it works:</p>
                <ul>
                    <li><strong>Spacebar:</strong> Reveal the answer.</li>
                    <li><strong>Keys 1-4:</strong> Grade your recall (1: Again, 2: Hard, 3: Good, 4: Easy).</li>
                    <li><strong>Cloze Deletion:</strong> Click on hidden terms [...] to peek at them before revealing the full rule.</li>
                    <li><strong>Keynotes:</strong> Add personal mnemonics or notes to any rule by clicking "Add Keynote" after revealing.</li>
                </ul>
            </div>
        </div>
    </div>
);

function DeNovoView({ 
    userId, allRulesBySubject, deNovoData, onSessionEnd, onToggleFlag, userSettings, 
    sessionState, sessionType, onUpdateSessionState, todaysDocketEntry, onStartPhase, 
    onCardUpdate, dailyDueCount, onCreateCustomSession, onEditRule, seenHints, 
    onDismissHint, cramDecks, onStartCramDeck, onDeleteCramDeck, onResetCramDeck, onRenameCramDeck,
    onTriggerToast 
}) {
    const [localData, setLocalData] = useState(deNovoData);
    const [sessionSeenCount, setSessionSeenCount] = useState({}); 
    const [undoStack, setUndoStack] = useState([]);
    const [showInfo, setShowInfo] = useState(false);
    const [localSeenHints, setLocalSeenHints] = useState(seenHints || []);

    const handleLocalDismiss = (id) => {
        const newHints = [...new Set([...localSeenHints, id])];
        setLocalSeenHints(newHints);
        if (onDismissHint) {
            onDismissHint(id);
        }
    };

    useEffect(() => { setLocalData(deNovoData); }, [deNovoData]);
    const { queue, currentIndex, isRevealed, state, history } = sessionState;
    const currentRuleId = queue[currentIndex];
    const findRule = useCallback((id) => {
        for (const k in allRulesBySubject) { const r = allRulesBySubject[k].find(x => x.id === id); if (r) return { ...r, subject: k }; } return null;
    }, [allRulesBySubject]);
    const currentRule = useMemo(() => findRule(currentRuleId), [findRule, currentRuleId]);
    const tracerCounts = useMemo(() => {
        let cNew = 0, cLrn = 0, cRev = 0;
        queue.slice(currentIndex).forEach(id => {
            const d = localData[id]?.srs_data;
            if (!d?.lastReviewedDate) cNew++; else if (d.learningStepIndex !== -1) cLrn++; else cRev++;
        });
        return { new: cNew, learning: cLrn, review: cRev };
    }, [queue, currentIndex, localData]);
    const pushToUndoStack = useCallback((action) => { setUndoStack(prev => [...prev, action]); }, []);
    const handleUndo = useCallback(() => {
        if (undoStack.length === 0) return;
        const lastAction = undoStack[undoStack.length - 1];
        setUndoStack(prev => prev.slice(0, -1));

        if (lastAction.type === 'reveal') {
            onUpdateSessionState({ isRevealed: false });
        } else if (lastAction.type === 'grade') {
            // Revert the local optimistic update directly
            setLocalData(prev => ({
                ...prev,
                [lastAction.ruleId]: lastAction.prevData
            }));
            
            // Revert the session state (queue, index, etc.)
            onUpdateSessionState({ 
                queue: lastAction.prevQueue, 
                currentIndex: lastAction.prevIndex, 
                isRevealed: true, 
                history: lastAction.prevHistory 
            });

            // Revert the session's seen count for the card
            setSessionSeenCount(prev => ({ ...prev, [lastAction.ruleId]: lastAction.prevSeenCount }));
        }
    }, [undoStack, onUpdateSessionState]);
    const handleReveal = useCallback(() => { if (isRevealed) return; pushToUndoStack({ type: 'reveal' }); onUpdateSessionState({ isRevealed: true }); }, [isRevealed, pushToUndoStack, onUpdateSessionState]);
    
const handleRateCard = useCallback(async (rating) => {

        const ratingMap = { 'Again': 1, 'Hard': 2, 'Good': 3, 'Easy': 4 };
        const ratingVal = ratingMap[rating] || 0;

       if (userId) { 
            // 1. Log Activity
            storage.logActivity(userId, 'rate_card', currentRuleId, ratingVal, {
                subject: currentRule.subject,
                is_hfr: currentRule.isHFR
            });

            // 2. Add Points (1 pt per review)
            addPoints(userId, 1);
            
            // 3. Trigger visual feedback
            // 3. Trigger visual feedback (UPDATED)
            if (onTriggerToast) {
                // Customize message based on rating
                let contextMessage = "+1 Point";
                if (rating === 'Easy') contextMessage = "+1 (Mastery)";
                else if (rating === 'Again') contextMessage = "+1 (Effort)";
                else if (currentRule.isHFR) contextMessage = "+1 (HFR Review)";
                
                // We pass a string now, forcing the toast to render text
                onTriggerToast(contextMessage); 
            }
        }

        const deadline = userSettings.targetMasteryDate ? new Date(userSettings.targetMasteryDate) : null;
        const currentSrData = localData[currentRuleId]?.srs_data || {};
        const optimisticNextSRS = srsService.calculateNewSRData(currentSrData, rating, deadline);
        
        const currentCount = (sessionSeenCount[currentRuleId] || 0) + 1;
        const isUnstable = optimisticNextSRS.currentInterval === 0 || optimisticNextSRS.learningStepIndex !== -1;
        const needsRepetition = (rating === 'Again' || rating === 'Hard' || (isUnstable && rating === 'Good' && currentCount < 2));

        const undoSnapshot = { 
            type: 'grade', 
            ruleId: currentRuleId, 
            prevData: localData[currentRuleId], 
            prevQueue: [...queue], 
            prevIndex: currentIndex, 
            prevHistory: [...(history || [])], 
            prevSeenCount: sessionSeenCount[currentRuleId] || 0 
        };
        pushToUndoStack(undoSnapshot);
        setSessionSeenCount(prev => ({ ...prev, [currentRuleId]: currentCount }));

        let newQueue = [...queue];
        if (needsRepetition) {
            const queueLen = newQueue.length - currentIndex;
            if (currentIndex + 1 >= newQueue.length) { 
                newQueue.push(currentRuleId); 
            } else { 
                const offset = rating === 'Again' ? 3 : Math.max(2, Math.floor(queueLen * 0.05)); 
                newQueue.splice(Math.min(newQueue.length, currentIndex + 1 + offset), 0, currentRuleId); 
            }
        }

        storage.rateCardViaEdge(currentRuleId, rating)
            .then(serverData => {
                onCardUpdate(currentRuleId, { ...localData[currentRuleId], srs_data: serverData.srs_data, status: serverData.status });
            })
            .catch(err => {
                console.warn("Background sync failed, using optimistic data", err);
                onCardUpdate(currentRuleId, { ...localData[currentRuleId], srs_data: optimisticNextSRS });
            });

        const updatedLocalData = { ...localData, [currentRuleId]: { ...localData[currentRuleId], srs_data: optimisticNextSRS } };
        setLocalData(updatedLocalData);
        
        const nextIdx = currentIndex + 1;
        if (nextIdx >= newQueue.length) { 
            onSessionEnd(sessionType, updatedLocalData, sessionState, [], true); 
        } else { 
            onUpdateSessionState({ queue: newQueue, currentIndex: nextIdx, isRevealed: false, history: [...(history || []), { id: currentRuleId, idx: currentIndex }] }); 
        }
    }, [currentRuleId, queue, currentIndex, localData, sessionSeenCount, onUpdateSessionState, onSessionEnd, sessionType, userSettings, onCardUpdate, history, pushToUndoStack, userId, currentRule]);

    useEffect(() => {
        if (state !== 'active') return;
        const handleKeyDown = (e) => {
            if (['INPUT', 'TEXTAREA'].includes(e.target.tagName)) return;
            
            // Undo functionality
            if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
                e.preventDefault();
                handleUndo();
                return;
            }

            if (e.code === 'Space') { 
                e.preventDefault(); 
                if (!isRevealed) handleReveal(); 
            } else if (isRevealed) {
                switch(e.key) { 
                    case '1': handleRateCard('Again'); break; 
                    case '2': handleRateCard('Hard'); break; 
                    case '3': handleRateCard('Good'); break; 
                    case '4': handleRateCard('Easy'); break; 
                    default: break; 
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [state, isRevealed, handleReveal, handleRateCard, handleUndo]);;

    if (state !== 'active') { 
        return <DeNovoHomeScreen 
        todaysDocketEntry={todaysDocketEntry} 
        onStartPhase={onStartPhase} 
        dailyDueCount={dailyDueCount}
        deNovoData={deNovoData}
        allRulesBySubject={allRulesBySubject}
        onCreateCustomSession={onCreateCustomSession}
        cramDecks={cramDecks}
        onStartCramDeck={onStartCramDeck}
        onDeleteCramDeck={onDeleteCramDeck}
        onResetCramDeck={onResetCramDeck}
        onRenameCramDeck={onRenameCramDeck}
    />; 
    }
    if (!currentRule) return null;
    const isNewCard = !localData[currentRuleId]?.srs_data?.lastReviewedDate;

    return (
        <div className="zen-mode-layout theme-aware">
            {showInfo && <InfoModal onClose={() => setShowInfo(false)} />}
            <div className="zen-top-bar">
                <TracerBar counts={tracerCounts} />
                <div className="zen-actions">
                    <button className={`icon-btn ${currentRule.isFlagged ? 'active' : ''}`} onClick={() => onToggleFlag(currentRule.id)} title="Flag for later"><span className="material-symbols-outlined">flag</span></button>
                    <button className="icon-btn" onClick={handleUndo} disabled={undoStack.length === 0} title="Undo last action"><span className="material-symbols-outlined">undo</span></button>
                    <button className="icon-btn" onClick={() => setShowInfo(true)} title="Help"><span className="material-symbols-outlined">info</span></button>
                    <button className="icon-btn" onClick={() => onSessionEnd(sessionType, localData, sessionState)} title="End Session"><span className="material-symbols-outlined">close</span></button>
                </div>
            </div>
            <ZenCard rule={currentRule} isRevealed={isRevealed} onReveal={handleReveal} onEditRule={onEditRule} />
            <div className="zen-controls-container">
                {isRevealed ? (
                    <OnboardingTooltip id="hint_denovo_rating" seenHints={localSeenHints} onDismiss={handleLocalDismiss} position="top" text="Be honest. If you forgot a single element, hit 'Again'. The algorithm needs to know to properly schedule this card for the future.">
                        <div className="rating-group">
                            <button className="zen-btn again" onClick={() => handleRateCard('Again')}><span className="btn-sub">Blackout (1)</span>1</button>
                            <button className="zen-btn hard" onClick={() => handleRateCard('Hard')}><span className="btn-sub">Struggled (2)</span>2</button>
                            <button className="zen-btn good" onClick={() => handleRateCard('Good')}><span className="btn-sub">Correct (3)</span>3</button>
                            <button className="zen-btn easy" onClick={() => handleRateCard('Easy')}><span className="btn-sub">Instant (4)</span>4</button>
                        </div>
                    </OnboardingTooltip>
                ) : (
                    <button className={`zen-btn show-answer ${isNewCard ? 'new-reveal' : ''}`} onClick={handleReveal}>{isNewCard ? "REVEAL RULE" : "SHOW ANSWER"}</button>
                )}
            </div>
        </div>
    );
}

export default DeNovoView;